import React, { useRef, useState, lazy, Suspense } from "react";
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from 'react-bootstrap';
import API from '../../api/api.jsx';
import CustomToast from "../../components/toast/toast.jsx";

// 상세화면의 버튼들
const Detail001 = ({ customerInfo }) => {
	const { t }                       = useTranslation();
	const { loanReceivableId }        = useParams();
	const [showModal, setShowModal]   = useState(false);
	const [modalTitle, setModalTitle] = useState('');

	// Define a state to hold the data to save
	const childComponent          = useRef(null);
    const [saveData, setSaveData] = useState(null);

	// 토스트
	const [toast, setToast] = useState(false);
	const [text, setText]   = useState('');

	const handleButtonClick = (title) => {
		// 납부기한연장 모달의 경우 원금분할상환 데이터 일시 alert로 대체
		if (title === commonModalTitles[2]) {
			const repayment = customerInfo && customerInfo.repayment_method;

			if (repayment === 'M02') {
				alert('원금분할상환 채권은 납부기한연장을 할 수 없습니다.');
				return;
			};
		}

		setModalTitle(title);
		setShowModal(true);
	};

	const handleClose = () => {
		if (showModal) {
			setShowModal(false);
		}
	};

	let isHandlingClick = false; // 클릭 처리 여부를 나타내는 변수

	// 부모 컴포넌트에서 콜백 함수 정의
	const handleSaveClick = async () => {
		if (isHandlingClick) return; // 클릭 처리 중이면 중복 클릭 방지

		isHandlingClick = true; // 클릭 처리 시작

		try {
			if (childComponent.current) {
				const childData = childComponent.current.getChildData();
				// console.log(childData);

				if (childData) {
					const division    = childData.btnValue;

					// 함수 실행
					const apiFunction = getApiFunction(division);

					await apiFunction({ loanReceivableId, childData });
				} else {
					console.log("전달받은 데이터 X");
				}
			}
		} catch (error) {
			console.error('API 호출 오류:', error);
		} finally {
			isHandlingClick = false; // 클릭 처리 종료
		}
	}

	// 구분자에 따른 호출할 함수 분리 구간
	const getApiFunction = (division) => {
		const apiFunctions = {
			'001': API.fetchBondDetailButton001Save,
			'002': API.fetchBondDetailButton002Save,
			'003': API.fetchBondDetailButton003Save,
			'004': API.fetchBondDetailButton004Save,
			// '005': API.fetchBondDetailButton005Save,
			'006': API.fetchBondDetailButton006Save,
		};
	
		return async (data) => {
			const { loanReceivableId, childData } = data;
			try {
				const result = await apiFunctions[division]({
					loanReceivableId: loanReceivableId.split('=')[1],
					childData,
				});

				// Toast에 담을 문구
				const SENT_DIVISIONS = ['006'];
				// const SENT_DIVISIONS = ['005', '006'];
				const text = SENT_DIVISIONS.includes(division) ? '발송이 완료되었습니다.' : '저장이 완료되었습니다.';
				setText(text);

				// // 결과 객체에 따라 알럴 표시
				// if (division ==='001' && result && result.content && result.content.message) {
				// 	alert(result.content.message);
				// }				
				// Toast 표시
				setToast(true);

				setShowModal(false);
	
				return true;
			} catch (error) {
				console.error(`Error fetching Bond Detail Button ${division} save:`, error);

				// 500 에러 발생
				if (error.response && error.response.status === 500) {
					const message = error.response.data.result.content.message;

					if (message) {
						alert(message);
					} else {
						alert(error.response.data.message);
					}
					return;
				}
				throw error;
			}
		};
	};

	// 버튼의 타이틀
	const commonModalTitles = [
		t(`LOAN_RECEIVABLE_INQUIRY.이자 수기 수납`, { defaultValue: '이자 수기 수납' }),
		t(`LOAN_RECEIVABLE_INQUIRY.상담내역 입력`, { defaultValue: '상담내역 입력' }),
		t(`LOAN_RECEIVABLE_INQUIRY.납부기한연장`, { defaultValue: '납부기한연장' }),
		t(`LOAN_RECEIVABLE_INQUIRY.가지급금 입력`, { defaultValue: '가지급금 입력' }),
		// t(`LOAN_RECEIVABLE_INQUIRY.Email 발송`, { defaultValue: 'Email 발송' }),
		t(`LOAN_RECEIVABLE_INQUIRY.Zalo 메시지 발송`, { defaultValue: 'Zalo 메시지 발송' }),
	];

	// 항목별 동적으로 불러올 내용.jsx
	const dynamicImports = {
		// [commonModalTitles[0]]: lazy(() => import("./detail_001_01_backup1.jsx")),
		[commonModalTitles[0]]: lazy(() => import("./detail_001_01.jsx")),
		[commonModalTitles[1]]: lazy(() => import("./detail_001_02.jsx")),
		[commonModalTitles[2]]: lazy(() => import("./detail_001_03.jsx")),
		[commonModalTitles[3]]: lazy(() => import("./detail_001_04.jsx")),
		// [commonModalTitles[4]]: lazy(() => import("./detail_001_05.jsx")),
		[commonModalTitles[4]]: lazy(() => import("./detail_001_06.jsx")),
	};

	const DynamicModalComponent = dynamicImports[modalTitle];

	// 모달 재사용을 위한 부분
	const CustomModal = ({ showModal, handleClose, title, children }) => {
		// Email과 Zalo는 버튼명이 저장이 아니라 발송
		// const isSpecialTitle = title === commonModalTitles[4] || title === commonModalTitles[5];
		const isSpecialTitle = title === commonModalTitles[4];

		return (
			<Modal show={showModal} onHide={handleClose} centered dialogClassName="modal-lg" backdrop="static">
				<Modal.Header closeButton>
					<Modal.Title>{title}</Modal.Title>
				</Modal.Header>

				<Modal.Body>
					{children}
				</Modal.Body>

				<Modal.Footer>
					{isSpecialTitle ? (
						<>
							<Button variant="success" onClick={handleSaveClick}>
								{t(`LOAN_RECEIVABLE_INQUIRY.발송`, { defaultValue: '발송' })}
							</Button>

							<Button variant="secondary" onClick={handleClose}>
								{t(`APPROVAL.닫기`, { defaultValue: '닫기' })}
							</Button>
						</>
					) : (
						<>
							<Button variant="success" onClick={handleSaveClick}>
								{t(`APPROVAL.저장`, { defaultValue: '저장' })}
							</Button>

							<Button variant="secondary" onClick={handleClose}>
								{t(`APPROVAL.닫기`, { defaultValue: '닫기' })}
							</Button>
						</>
					)}
				</Modal.Footer>
			</Modal>
		);
	};

	return (
		<>
		  <CustomToast show={toast} setToast={setToast} text={text} />
	  
		  <div className="row justify-content-center">
			{commonModalTitles.map((buttonText, index) => (
			  <div className="col-md-2 mb-3" key={index}>
				<button className="btn btn-secondary w-100" onClick={() => handleButtonClick(buttonText)}>
				  {buttonText}
				</button>
			  </div>
			))}
		  </div>
	  
		  {/* Modal Component */}
		  {DynamicModalComponent && (
			<Suspense>
			  <CustomModal showModal={showModal} handleClose={handleClose} title={modalTitle}>
				<DynamicModalComponent customerInfo={customerInfo} ref={childComponent} />
			  </CustomModal>
			</Suspense>
		  )}
		</>
	  );
};

export default Detail001;